import {
  questionBombModes,
  type QuestionBombCustomMission,
  type QuestionBombCustomItem,
  type QuestionBombModeId,
} from "../data/questionBombItems";

const STORAGE_KEY = "randombomb:question-bomb:v1";
const RECENT_IDS_LIMIT = 8;
const DEFAULT_MODE_ID = questionBombModes[0]?.id ?? "warmup";
const CUSTOM_ONLY_MODE_ID: QuestionBombModeId = "customOnly";
const LEGACY_DEFAULT_CUSTOM_CATEGORY = "내 질문";
const DEFAULT_CUSTOM_MODE_MISSION_EVERY = 4;

export type QuestionBombStorageState = {
  selectedModeId: QuestionBombModeId;
  excludeRecent: boolean;
  favoriteQuestionIds: string[];
  recentQuestionIds: string[];
  customQuestions: QuestionBombCustomItem[];
  customMissions: QuestionBombCustomMission[];
  customModeMissionsEnabled: boolean;
  customModeMissionEvery: number;
};

const createDefaultStorageState = (): QuestionBombStorageState => ({
  selectedModeId: DEFAULT_MODE_ID,
  excludeRecent: true,
  favoriteQuestionIds: [],
  recentQuestionIds: [],
  customQuestions: [],
  customMissions: [],
  customModeMissionsEnabled: true,
  customModeMissionEvery: DEFAULT_CUSTOM_MODE_MISSION_EVERY,
});

const isModeId = (value: unknown): value is QuestionBombModeId =>
  questionBombModes.some((mode) => mode.id === value);

const sanitizeStringArray = (value: unknown) => {
  if (!Array.isArray(value)) return [];

  return value.filter((item): item is string => typeof item === "string");
};

const sanitizeMissionEvery = (value: unknown) => {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    return DEFAULT_CUSTOM_MODE_MISSION_EVERY;
  }

  return Math.min(Math.max(value, 2), 8);
};

const sanitizeCustomQuestions = (value: unknown): QuestionBombCustomItem[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (
      !item ||
      typeof item !== "object" ||
      !("id" in item) ||
      !("modeId" in item) ||
      !("category" in item) ||
      !("text" in item) ||
      !("createdAt" in item)
    ) {
      return [];
    }

    const nextItem = item as {
      id: unknown;
      modeId: unknown;
      category: unknown;
      text: unknown;
      createdAt: unknown;
    };

    if (
      typeof nextItem.id !== "string" ||
      !isModeId(nextItem.modeId) ||
      typeof nextItem.category !== "string" ||
      typeof nextItem.text !== "string" ||
      typeof nextItem.createdAt !== "number"
    ) {
      return [];
    }

    return [
      {
        id: nextItem.id,
        modeId: CUSTOM_ONLY_MODE_ID,
        category:
          nextItem.category.trim() === LEGACY_DEFAULT_CUSTOM_CATEGORY
            ? ""
            : nextItem.category.trim(),
        text: nextItem.text,
        createdAt: nextItem.createdAt,
      },
    ];
  });
};

const sanitizeCustomMissions = (value: unknown): QuestionBombCustomMission[] => {
  if (!Array.isArray(value)) return [];

  return value.flatMap((item) => {
    if (
      !item ||
      typeof item !== "object" ||
      !("id" in item) ||
      !("modeId" in item) ||
      !("title" in item) ||
      !("description" in item) ||
      !("note" in item) ||
      !("createdAt" in item)
    ) {
      return [];
    }

    const nextItem = item as {
      id: unknown;
      modeId: unknown;
      title: unknown;
      description: unknown;
      note: unknown;
      createdAt: unknown;
    };

    if (
      typeof nextItem.id !== "string" ||
      !isModeId(nextItem.modeId) ||
      typeof nextItem.title !== "string" ||
      typeof nextItem.description !== "string" ||
      typeof nextItem.note !== "string" ||
      typeof nextItem.createdAt !== "number"
    ) {
      return [];
    }

    const nextMission: QuestionBombCustomMission = {
      id: nextItem.id,
      modeId: CUSTOM_ONLY_MODE_ID,
      title: nextItem.title.trim(),
      description: nextItem.description.trim(),
      note: nextItem.note.trim(),
      createdAt: nextItem.createdAt,
    };

    if (nextMission.title.length === 0 || nextMission.description.length === 0) {
      return [];
    }

    return [nextMission];
  });
};

export const loadQuestionBombStorage = (): QuestionBombStorageState => {
  if (typeof window === "undefined") {
    return createDefaultStorageState();
  }

  try {
    const rawStorage = window.localStorage.getItem(STORAGE_KEY);
    if (!rawStorage) return createDefaultStorageState();

    const parsedStorage = JSON.parse(rawStorage) as Partial<QuestionBombStorageState>;

    return {
      selectedModeId: DEFAULT_MODE_ID,
      excludeRecent:
        typeof parsedStorage.excludeRecent === "boolean"
          ? parsedStorage.excludeRecent
          : true,
      favoriteQuestionIds: Array.from(
        new Set(sanitizeStringArray(parsedStorage.favoriteQuestionIds))
      ),
      recentQuestionIds: Array.from(
        new Set(sanitizeStringArray(parsedStorage.recentQuestionIds))
      ).slice(0, RECENT_IDS_LIMIT),
      customQuestions: sanitizeCustomQuestions(parsedStorage.customQuestions),
      customMissions: sanitizeCustomMissions(parsedStorage.customMissions),
      customModeMissionsEnabled:
        typeof parsedStorage.customModeMissionsEnabled === "boolean"
          ? parsedStorage.customModeMissionsEnabled
          : true,
      customModeMissionEvery: sanitizeMissionEvery(
        parsedStorage.customModeMissionEvery
      ),
    };
  } catch {
    return createDefaultStorageState();
  }
};

export const saveQuestionBombStorage = (state: QuestionBombStorageState) => {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        excludeRecent: state.excludeRecent,
        favoriteQuestionIds: Array.from(new Set(state.favoriteQuestionIds)),
        recentQuestionIds: Array.from(new Set(state.recentQuestionIds)).slice(
          0,
          RECENT_IDS_LIMIT
        ),
        customQuestions: state.customQuestions,
        customMissions: state.customMissions,
        customModeMissionsEnabled: state.customModeMissionsEnabled,
        customModeMissionEvery: sanitizeMissionEvery(
          state.customModeMissionEvery
        ),
      })
    );
  } catch {
    // localStorage 사용이 불가능한 환경에서는 조용히 무시합니다.
  }
};
